/*
 * Eigenes, vom Prestige-Theme unabhängiges Skript für die FAQ-Sektion (sections/feschd-faq.liquid).
 * Steuert drei Ansichten: Kategorie-Übersicht (Buttons), eine geöffnete Kategorie (alle Fragen als
 * Accordion) und die Sucheergebnisse (über alle Kategorien hinweg gefiltert).
 *
 * Suche: die Eingabe wird in Wörter zerlegt, gängige deutsche Füllwörter werden entfernt, die restlichen
 * Stichwörter werden einzeln (ODER-Verknüpfung) gegen Frage+Antwort abgeglichen. Eine Frage passt schon bei
 * einem Treffer - Reihenfolge/Formulierung der Eingabe spielt keine Rolle. Je mehr Stichwörter passen, desto
 * höher die Relevanz; Treffer werden je Kategorie per CSS-"order" nach Relevanz sortiert (ohne DOM-Umbau).
 */

const FESCHD_FAQ_STOPWORDS = new Set([
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer', 'eines', 'einem', 'einen',
  'und', 'oder', 'aber', 'doch', 'auch', 'noch', 'nur', 'schon', 'so', 'als', 'wie', 'was', 'wo',
  'wer', 'wen', 'wem', 'wann', 'warum', 'wieso', 'weshalb', 'welche', 'welcher', 'welches',
  'ist', 'sind', 'war', 'waren', 'sein', 'seine', 'seiner', 'seinen', 'bin', 'bist', 'wird',
  'werden', 'wurde', 'wurden', 'habe', 'hast', 'hat', 'haben', 'hatte', 'hatten', 'kann', 'kannst',
  'können', 'könnt', 'konnte', 'soll', 'sollst', 'sollen', 'muss', 'musst', 'müssen', 'darf', 'dürfen',
  'ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr', 'mein', 'meine', 'meinen', 'meiner', 'meinem',
  'dein', 'deine', 'deinen', 'deiner', 'deinem', 'uns', 'euch', 'ihm', 'ihn', 'ihnen',
  'für', 'mit', 'ohne', 'von', 'vom', 'zu', 'zum', 'zur', 'im', 'in', 'ins', 'am', 'an', 'auf',
  'bei', 'aus', 'durch', 'um', 'bis', 'über', 'unter', 'vor', 'nach', 'gegen', 'ja', 'nein',
  'nicht', 'dass', 'sich', 'man', 'euer', 'eure',
]);

function feschdFaqTokenize(text) {
  return text
    .toLowerCase()
    .replace(/[.,!?;:()"'’„“]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

class FeschdFaq extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('[data-feschd-faq-search]');
    this.backButton = this.querySelector('[data-feschd-faq-back]');
    this.overview = this.querySelector('[data-feschd-faq-overview]');
    this.detail = this.querySelector('[data-feschd-faq-detail]');
    this.categorySections = this.querySelectorAll('[data-feschd-faq-category]');
    this.categoryButtons = this.querySelectorAll('[data-feschd-faq-open]');
    this.noResults = this.querySelector('[data-feschd-faq-no-results]');

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => this.openCategory(button.dataset.feschdFaqOpen));
    });

    if (this.backButton) {
      this.backButton.addEventListener('click', () => this.showOverview());
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearch());
    }
  }

  resetQuestionVisibility() {
    this.querySelectorAll('[data-feschd-faq-question]').forEach((question) => {
      question.hidden = false;
      question.style.order = '';
    });
  }

  showOverview() {
    if (this.searchInput) this.searchInput.value = '';
    this.resetQuestionVisibility();

    if (this.overview) this.overview.hidden = false;
    if (this.detail) this.detail.hidden = true;
    if (this.backButton) this.backButton.hidden = true;
    if (this.noResults) this.noResults.hidden = true;

    this.categorySections.forEach((section) => {
      section.hidden = true;
    });
  }

  openCategory(id) {
    this.resetQuestionVisibility();

    if (this.overview) this.overview.hidden = true;
    if (this.detail) this.detail.hidden = false;
    if (this.backButton) this.backButton.hidden = false;
    if (this.noResults) this.noResults.hidden = true;

    this.categorySections.forEach((section) => {
      section.hidden = section.dataset.feschdFaqCategory !== id;
    });
  }

  handleSearch() {
    const rawQuery = this.searchInput.value.trim();

    if (!rawQuery) {
      this.showOverview();
      return;
    }

    if (this.overview) this.overview.hidden = true;
    if (this.detail) this.detail.hidden = false;
    if (this.backButton) this.backButton.hidden = false;

    const queryWords = feschdFaqTokenize(rawQuery);
    const keywords = queryWords.filter((word) => word.length > 1 && !FESCHD_FAQ_STOPWORDS.has(word));
    const effectiveKeywords = keywords.length > 0 ? keywords : queryWords;

    let anyMatch = false;

    this.categorySections.forEach((section) => {
      let categoryHasMatch = false;

      section.querySelectorAll('[data-feschd-faq-question]').forEach((question) => {
        const text = question.dataset.feschdFaqSearchText;
        const score = effectiveKeywords.reduce((sum, word) => (text.includes(word) ? sum + 1 : sum), 0);

        question.hidden = score === 0;
        question.style.order = score === 0 ? '' : String(-score);

        if (score > 0) categoryHasMatch = true;
      });

      section.hidden = !categoryHasMatch;
      if (categoryHasMatch) anyMatch = true;
    });

    if (this.noResults) this.noResults.hidden = anyMatch;
  }
}

if (!window.customElements.get('feschd-faq')) {
  window.customElements.define('feschd-faq', FeschdFaq);
}
